import { Button } from "@shared-ui/button";
import { useMutation } from "@tanstack/react-query";
import { Dialog } from "@/components/ui/dialog";
import {
	type AdminVariant,
	retireVariant,
} from "@/features/catalog/services/catalog-api.service";
import { apiErrorMessage } from "@/lib/api-error";

/**
 * Confirm retiring a variant.
 *
 * Its own component for the reason `remove-template-dialog.tsx` is: this is not
 * a yes/no box, it is the only place the consequences of an irreversible action
 * are stated, and burying that inside a list component is how the wording rots.
 *
 * WHAT THE WORD "RETIRE" DOES NOT SAY, and this dialog therefore must. The
 * delete is soft server-side -- the row survives so quoted BOQ lines keep the
 * link to which finish or size they were for -- but every variant read filters
 * `deleted_at is null`, so from the admin it is gone: no endpoint returns it,
 * there is no "show retired" filter, and bringing it back needs an engineer with
 * a database connection. An operator reading "retire" could reasonably assume
 * otherwise.
 *
 * The one thing that IS recoverable is the SKU. `uq_product_variants_sku` is
 * scoped to `deleted_at is null`, so the code can be used again immediately --
 * worth saying, because an operator retiring a mistyped variant wants to know
 * they can retype it.
 *
 * The mutation lives HERE and the list renders this with a `key` of the variant
 * id, both load-bearing for the reasons the template dialog gives.
 */

const TITLE_ID = "retire-variant-dialog-title";

interface RetireVariantDialogProps {
	variant: AdminVariant;
	onClose: () => void;
	onRetired: () => Promise<void> | void;
}

export function RetireVariantDialog({
	variant,
	onClose,
	onRetired,
}: RetireVariantDialogProps) {
	const retire = useMutation({
		mutationFn: () => retireVariant(variant.id),
		onSuccess: async () => {
			await onRetired();
			onClose();
		},
	});

	return (
		<Dialog
			open
			titleId={TITLE_ID}
			onClose={() => {
				if (!retire.isPending) onClose();
			}}
			className="grid w-full max-w-lg gap-4 rounded-3xl border bg-white p-5 shadow-xl"
		>
			<div className="grid gap-2">
				<h2 id={TITLE_ID} className="text-lg font-semibold">
					Retire {variant.sku}?
				</h2>
				<p className="text-sm text-muted-foreground">
					This cannot be undone from the admin. The variant disappears from this
					product and from customer browsing, and there is no screen that brings
					it back — restoring it needs an engineer.
				</p>
				<p className="text-sm text-muted-foreground">
					The SKU <span className="font-medium">{variant.sku}</span> becomes
					free to use again straight away.
				</p>
			</div>

			{retire.isError ? (
				<p role="alert" className="text-sm text-red-700">
					{apiErrorMessage(retire.error, "Could not retire this variant.")}
				</p>
			) : null}

			<div className="flex justify-end gap-2 border-t pt-3">
				<Button variant="ghost" onClick={onClose} disabled={retire.isPending}>
					Cancel
				</Button>
				<Button
					variant="destructive"
					onClick={() => retire.mutate()}
					disabled={retire.isPending}
				>
					{retire.isPending ? "Retiring…" : "Retire variant"}
				</Button>
			</div>
		</Dialog>
	);
}
